import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Stack, Typography, FormControl, InputLabel, Select, MenuItem,
  Button, Switch, FormControlLabel, Alert, Divider, useTheme, alpha, Skeleton
} from '@mui/material';
import { TrendingUp, AddCircleOutline, ArrowForward } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { Session } from '../../types/sessions';
import { sessionsApi } from '../../services/sessions/sessionsApi';
import { sectionInstancesApi } from '../../services/sessions/sectionInstancesApi';
import { curriculumApi } from '../../services/sessions/curriculumApi';
import { enrollmentsApi } from '../../services/sessions/enrollmentsApi';
import { promotionsApi } from '../../services/sessions/promotionsApi';
import CreateTargetSessionDialog from './CreateTargetSessionDialog';

interface PromotionSetupStepProps {
  sourceSession: Session;
  onRunStarted: (runId: string) => void;
}

const PromotionSetupStep: React.FC<PromotionSetupStepProps> = ({ sourceSession, onRunStarted }) => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const [planningSessions, setPlanningSessions] = useState<Session[]>([]);
  const [targetSessionId, setTargetSessionId]   = useState<string>('');
  const [copySections, setCopySections]   = useState<boolean>(true);
  const [copyCurriculum, setCopyCurriculum] = useState<boolean>(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);
  // Enrolled-student count in the source session — drives the "{N} students
  // will be processed" hint and disables the Continue button when zero.
  const [enrolledCount, setEnrolledCount] = useState<number | null>(null);

  const loadPlanning = async (): Promise<Session[]> => {
    const all = await sessionsApi.list({ includeArchived: false });
    return all.filter(s => s.status === 'planning' && s._id !== sourceSession._id);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        // Two parallel reads: planning sessions for the target picker, and
        // the source-session enrollment count for the safety hint.
        const [list, headcount] = await Promise.all([
          loadPlanning(),
          enrollmentsApi.list({
            sessionId: sourceSession._id,
            status: 'enrolled',
            limit: 1
          }).then(r => r.pagination?.totalItems ?? r.data.length).catch(() => 0)
        ]);
        if (cancelled) return;
        setPlanningSessions(list);
        setEnrolledCount(headcount);
        // Auto-pick the first planning session, if any.
        if (list.length === 1) setTargetSessionId(list[0]._id);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceSession._id]);

  const targetSession = useMemo(
    () => planningSessions.find(s => s._id === targetSessionId) ?? null,
    [planningSessions, targetSessionId]
  );

  const handleTargetCreated = async (created: Session) => {
    const list = await loadPlanning();
    setPlanningSessions(list);
    setTargetSessionId(created._id);
  };

  const startRun = async () => {
    if (!targetSessionId) return;
    setSubmitting(true);
    try {
      // Optional: copy section structure first, then curriculum, then start the run.
      if (copySections) {
        try { await sectionInstancesApi.bulkCopyFromSession({
          sourceSessionId: sourceSession._id, targetSessionId
        }); } catch { /* surfaces in main toast below */ }
      }
      if (copyCurriculum) {
        try { await curriculumApi.copyFromSession({
          sourceSessionId: sourceSession._id, targetSessionId
        }); } catch { /* same */ }
      }
      const run = await promotionsApi.start({
        sourceSessionId: sourceSession._id,
        targetSessionId
      });
      enqueueSnackbar('Promotion run started — choose decisions per class next.', { variant: 'success' });
      onRunStarted(run._id);
    } catch (err: any) {
      enqueueSnackbar(err?.message || 'Could not start the run', { variant: 'error' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 720, mx: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: 'background.paper',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.22)}`,
          boxShadow: `0 1px 2px ${alpha(theme.palette.common.black, 0.04)}, 0 12px 32px -12px ${alpha(theme.palette.primary.main, 0.2)}`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.45)} 100%)`,
          },
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '50%',
            bgcolor: alpha(theme.palette.primary.main, 0.14),
            color: theme.palette.primary.main,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
          }}>
            <TrendingUp />
          </Box>
          <Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
              Step 1 · Set up the run
            </Typography>
            <Typography variant="h5" fontWeight={700}>Promote from {sourceSession.name}</Typography>
          </Box>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pick where students from <strong>{sourceSession.name}</strong> will land next. You can leave and come back
          — the run resumes from where you left off, class by class.
        </Typography>

        {/* Source-session headcount — drives the warning + button gate below */}
        {enrolledCount !== null && (
          enrolledCount === 0 ? (
            <Alert severity="warning" variant="outlined" sx={{ mb: 3 }}>
              <strong>{sourceSession.name}</strong> has no enrolled students.
              There's nothing to promote — create enrollments first.
            </Alert>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 3 }}>
              <strong>{enrolledCount}</strong> enrolled student{enrolledCount === 1 ? '' : 's'} will be processed
              from this session.
            </Typography>
          )
        )}

        <Stack spacing={3}>
          {/* Target session */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Target session</Typography>
            {loading ? (
              <Skeleton variant="rounded" height={56} />
            ) : (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <FormControl fullWidth disabled={planningSessions.length === 0}>
                  <InputLabel>{planningSessions.length === 0 ? 'No planning sessions yet' : 'Pick a planning session'}</InputLabel>
                  <Select
                    value={targetSessionId}
                    onChange={(e) => setTargetSessionId(e.target.value)}
                    label={planningSessions.length === 0 ? 'No planning sessions yet' : 'Pick a planning session'}
                  >
                    {planningSessions.map(s => (
                      <MenuItem key={s._id} value={s._id}>
                        {s.name}
                        <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                          · {format(new Date(s.startDate), 'MMM yyyy')}–{format(new Date(s.endDate), 'MMM yyyy')}
                        </Typography>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  startIcon={<AddCircleOutline />}
                  onClick={() => setCreateOpen(true)}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Create new
                </Button>
              </Stack>
            )}
            {targetSession && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Students will be promoted into <strong>{targetSession.name}</strong>.
              </Typography>
            )}
          </Box>

          <Divider />

          {/* Copy toggles */}
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Carry over from {sourceSession.name}</Typography>
            <Stack spacing={1}>
              <FormControlLabel
                control={<Switch checked={copySections} onChange={(_, v) => setCopySections(v)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Section structure</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Copy the same sections (A, B, Science, etc.) into the new session.
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={<Switch checked={copyCurriculum} onChange={(_, v) => setCopyCurriculum(v)} />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={500}>Curriculum</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Copy subject assignments per class and section.
                    </Typography>
                  </Box>
                }
              />
            </Stack>
            {!copySections && copyCurriculum && (
              <Alert severity="info" variant="outlined" sx={{ mt: 1.5 }}>
                Section-specific subjects need a matching section in the target. Without copying sections,
                only common subjects will be carried over.
              </Alert>
            )}
          </Box>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 4 }}>
          <Button
            variant="contained"
            size="large"
            disableElevation
            disabled={!targetSessionId || submitting || enrolledCount === 0}
            endIcon={<ArrowForward />}
            onClick={startRun}
          >
            {submitting ? 'Starting…' : 'Continue to decisions'}
          </Button>
        </Stack>
      </Paper>

      <CreateTargetSessionDialog
        open={createOpen}
        sourceSession={sourceSession}
        onClose={() => setCreateOpen(false)}
        onCreated={handleTargetCreated}
      />
    </Box>
  );
};

export default PromotionSetupStep;
